/**
 * Debug Capture Service
 *
 * Singleton service for managing debug capture sessions.
 * Use this service to capture payment-level state during processing.
 *
 * @example
 * // Start a capture session
 * DebugCapture.startSession('candidate-123', 'config-456');
 *
 * // During processing, check if capturing (called from callbacks)
 * if (DebugCapture.isCapturing('candidate-123')) {
 *   DebugCapture.recordPayment(context);
 * }
 *
 * // End the session and get captured data
 * const capture = DebugCapture.endSession();
 * if (capture) {
 *   DebugCapture.downloadAsJSON(capture);
 * }
 */

import { DebugFieldRegistry } from '../field-registry';
import { initializeFields } from '../fields';
import type {
  DebugCaptureConfig,
  DebugCapturePreset,
  DebugExtractionContext,
  DebugSessionCapture,
  DebugSessionSummary,
  PaymentSnapshot,
} from '../types';
import { DEBUG_SCHEMA_VERSION, DEFAULT_CAPTURE_CONFIG } from '../types';
import { buildPaymentSnapshot } from './snapshot-builder';

/**
 * Debug Capture Service - Singleton
 */
export class DebugCapture {
  private static instance: DebugCapture | null = null;

  // Current session state
  private currentSession: Partial<DebugSessionCapture> | null = null;
  private config: DebugCaptureConfig = { ...DEFAULT_CAPTURE_CONFIG };
  private startTime = 0;
  private captureEnabled = false;
  private targetCandidateId: string | null = null;

  private constructor() {
    // Ensure fields are initialized
    initializeFields();
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): DebugCapture {
    if (!DebugCapture.instance) {
      DebugCapture.instance = new DebugCapture();
    }
    return DebugCapture.instance;
  }

  /**
   * Reset the singleton (for testing)
   */
  static reset(): void {
    DebugCapture.instance = null;
  }

  // ========== Session Management ==========

  /**
   * Start a new capture session
   */
  static startSession(
    candidateId: string,
    configurationId: string,
    options?: {
      preset?: DebugCapturePreset;
      projectId?: string;
      candidateName?: string;
      millageShapeName?: string;
    }
  ): void {
    const instance = DebugCapture.getInstance();

    // End any existing session
    if (instance.captureEnabled) {
      console.warn('[DebugCapture] Previous session not ended, ending now');
      DebugCapture.endSession();
    }

    // Configure capture
    instance.config = {
      ...DEFAULT_CAPTURE_CONFIG,
      preset: options?.preset ?? 'standard',
    };

    // Initialize session
    instance.currentSession = {
      sessionId: `debug-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      schemaVersion: DEBUG_SCHEMA_VERSION,
      timestamp: Date.now(),
      candidateId,
      candidateName: options?.candidateName ?? candidateId,
      configurationId,
      projectId: options?.projectId,
      millageShapeName: options?.millageShapeName,
      capturePreset: instance.config.preset,
      enabledFieldIds: DebugFieldRegistry.getInstance()
        .getEnabledFields(instance.config)
        .map((f) => f.id),
      paymentSnapshots: [],
    };

    instance.startTime = performance.now();
    instance.captureEnabled = true;
    instance.targetCandidateId = candidateId;

    console.log(`[DebugCapture] Session started for candidate: ${candidateId}`);
  }

  /**
   * Check if capture is active for a given candidate
   */
  static isCapturing(candidateId: string): boolean {
    const instance = DebugCapture.getInstance();
    return instance.captureEnabled && instance.targetCandidateId === candidateId;
  }

  /**
   * Check if any capture is active
   */
  static isActive(): boolean {
    return DebugCapture.getInstance().captureEnabled;
  }

  /**
   * Get current configuration
   */
  static getConfig(): DebugCaptureConfig {
    return { ...DebugCapture.getInstance().config };
  }

  // ========== Data Recording ==========

  /**
   * Record a payment snapshot
   */
  static recordPayment(context: DebugExtractionContext): void {
    const instance = DebugCapture.getInstance();

    // Guard: not capturing
    if (!instance.captureEnabled || !instance.currentSession) {
      return;
    }

    // Guard: max payments reached
    if (
      instance.currentSession.paymentSnapshots &&
      instance.currentSession.paymentSnapshots.length >= instance.config.maxPayments
    ) {
      console.warn('[DebugCapture] Max payments reached, skipping');
      return;
    }

    // Build and record snapshot
    const snapshot = buildPaymentSnapshot(context, instance.config);
    instance.currentSession.paymentSnapshots?.push(snapshot);
  }

  /**
   * End the current session and return captured data
   */
  static endSession(): DebugSessionCapture | null {
    const instance = DebugCapture.getInstance();

    if (!instance.captureEnabled || !instance.currentSession) {
      return null;
    }

    // Calculate processing time
    const processingTimeMs = performance.now() - instance.startTime;

    // Build summary from captured data
    const summary = DebugCapture.buildSummary(instance.currentSession.paymentSnapshots ?? []);

    // Complete the session
    const capture: DebugSessionCapture = {
      sessionId: instance.currentSession.sessionId ?? '',
      schemaVersion: instance.currentSession.schemaVersion ?? DEBUG_SCHEMA_VERSION,
      timestamp: instance.currentSession.timestamp ?? Date.now(),
      processingTimeMs,
      candidateId: instance.currentSession.candidateId ?? '',
      candidateName: instance.currentSession.candidateName ?? '',
      configurationId: instance.currentSession.configurationId ?? '',
      projectId: instance.currentSession.projectId,
      millageShapeName: instance.currentSession.millageShapeName,
      capturePreset: instance.currentSession.capturePreset ?? 'standard',
      enabledFieldIds: instance.currentSession.enabledFieldIds ?? [],
      paymentSnapshots: instance.currentSession.paymentSnapshots ?? [],
      summary,
    };

    // Reset state
    instance.captureEnabled = false;
    instance.targetCandidateId = null;
    instance.currentSession = null;

    console.log(
      `[DebugCapture] Session ended. Captured ${capture.paymentSnapshots.length} payments in ${processingTimeMs.toFixed(2)}ms`
    );

    return capture;
  }

  // ========== Summary Building ==========

  /**
   * Build summary statistics from payment snapshots
   */
  private static buildSummary(snapshots: PaymentSnapshot[]): DebugSessionSummary {
    let totalRevenue = 0;
    let totalPrincipal = 0;
    let totalInterest = 0;
    let endingDRF = 0;
    let activeBondCount = 0;

    for (const snapshot of snapshots) {
      // Sum revenue
      const revenue = snapshot.fields['revenue.paymentPortion']?.value;
      if (typeof revenue === 'number') totalRevenue += revenue;

      // Sum principal
      const principal = snapshot.fields['principal.total']?.value;
      if (typeof principal === 'number') totalPrincipal += principal;

      // Sum interest
      const interest = snapshot.fields['interest.total']?.value;
      if (typeof interest === 'number') totalInterest += interest;

      // Track ending DRF (last snapshot)
      const drf = snapshot.fields['drf.ending']?.value;
      if (typeof drf === 'number') endingDRF = drf;

      // Track active bonds
      const bonds = snapshot.fields['interest.activeBondCount']?.value;
      if (typeof bonds === 'number' && bonds > activeBondCount) {
        activeBondCount = bonds;
      }
    }

    return {
      totalRevenue,
      totalPrincipal,
      totalInterest,
      endingDRF,
      isViable: true, // Will be updated from candidate results
      failureMessages: [],
      paymentsProcessed: snapshots.length,
      activeBondCount,
    };
  }

  /**
   * Update session summary with candidate results
   */
  static updateSummaryFromResults(isViable: boolean, failureMessages: string[]): void {
    const instance = DebugCapture.getInstance();
    if (instance.currentSession) {
      // Will be included when session ends
      instance.currentSession.summary = {
        ...(instance.currentSession.summary ?? {
          totalRevenue: 0,
          totalPrincipal: 0,
          totalInterest: 0,
          endingDRF: 0,
          paymentsProcessed: 0,
          activeBondCount: 0,
        }),
        isViable,
        failureMessages,
      };
    }
  }
}
